<script setup>
import { ref, computed, watch, useTemplateRef } from "vue";
import { infotainments, likes } from "api";

defineOptions({
  name: "InfotainmentCard",
});

const props = defineProps({
  infotainment: { type: Object, required: true },
});

const liked = ref(props.infotainment.liked);
const currentIndex = ref(0);
const slidesEl = useTemplateRef("slidesEl");

const hasSlider = computed(() => props.infotainment.slides.length > 1);
const hasCta = computed(
  () => !!props.infotainment.linkLabel && !!props.infotainment.linkUrl,
);
const lastIndex = computed(() => props.infotainment.slides.length - 1);

watch(() => props.infotainment.id, () => {
  slidesEl.value?.scrollTo({ left: 0, behavior: 'instant' });
  currentIndex.value = 0;
});

function scrollToIndex(index) {
  const el = slidesEl.value;
  const target = el?.children[index];
  if (!el || !target) return;
  el.scrollTo({ left: target.offsetLeft - el.offsetLeft, behavior: "smooth" });
}

function prev() {
  if (currentIndex.value === 0) return;
  currentIndex.value -= 1;
  scrollToIndex(currentIndex.value);
}

function next() {
  if (currentIndex.value >= lastIndex.value) return;
  currentIndex.value += 1;
  scrollToIndex(currentIndex.value);
}

function onScroll() {
  const el = slidesEl.value;
  if (!el) return;
  currentIndex.value = [...el.children]
    .map((c, i) => [Math.abs(c.offsetLeft - el.offsetLeft - el.scrollLeft), i])
    .reduce((a, b) => (a[0] < b[0] ? a : b))[1];
}

function slideClass(template) {
  return `template-${template.replace(/_/g, "-")}`;
}

async function onCtaClick() {
  try {
    await infotainments.trackCtaClick({
      params: { id: props.infotainment.id },
    });
  } catch (e) {
    console.error("InfotainmentCard: failed to track CTA click", e);
  }
}

async function toggleLike() {
  const previous = liked.value;
  liked.value = !previous;
  try {
    await likes.toggle({
      data: { recordType: "Infotainment", recordId: props.infotainment.id },
    });
  } catch (e) {
    liked.value = previous;
    console.error("InfotainmentCard: failed to toggle like", e);
  }
}
</script>

<template>
  <infotainment-slider class="slider-wrapper">
    <ul ref="slidesEl" class="slider-component" @scroll.passive="onScroll">
      <li v-for="slide in infotainment.slides" :key="slide.id">
        <infotainment-card :class="slideClass(slide.template)" infotainment="">
          <div class="content">
            <template v-if="slide.template === 'full_image'">
              <img :src="slide.mediaUrl" alt="" />
            </template>

            <template v-else-if="slide.template === 'title'">
              <h2 class="title-big">{{ slide.title }}</h2>
              <picture class="illustration">
                <img v-if="slide.mediaUrl" :src="slide.mediaUrl" alt="" />
              </picture>
            </template>

            <template v-else-if="slide.template === 'subtitle'">
              <h3 class="title-medium mb-2.5">{{ slide.title }}</h3>
              <div class="rich-text" v-html="slide.contentHtml"></div>
            </template>

            <template v-else-if="slide.template === 'content'">
              <div class="rich-text" v-html="slide.contentHtml"></div>
            </template>

            <template v-else-if="slide.template === 'media'">
              <picture v-if="slide.mediaUrl" class="picture mb-2.5">
                <img :src="slide.mediaUrl" alt="" />
              </picture>
              <div class="rich-text" v-html="slide.contentHtml"></div>
            </template>
          </div>

          <footer>
            <div class="flex gap-1 start">
              <button
                type="button"
                :class="[
                  'small-icon-button',
                  'control:like-icon',
                  { 'is-active': liked },
                ]"
                @click="toggleLike"
              >
                <font-icon class="like"></font-icon>
                <font-icon class="like--fill"></font-icon>
              </button>
              <a
                v-if="hasCta"
                :href="infotainment.linkUrl"
                class="button is-secondary"
                target="_blank"
                rel="noopener"
                @click="onCtaClick"
              >
                {{ infotainment.linkLabel }}
              </a>
            </div>

            <nav v-if="hasSlider" class="gap-1 end hidden lg:flex">
              <button
                  class="small-icon-button is-light start"
                  type="button"
                  :disabled="currentIndex === 0"
                  @click="prev"
              >
                <font-icon class="previous"></font-icon>
              </button>
              <button
                  class="small-icon-button is-light end"
                  type="button"
                  :disabled="currentIndex >= lastIndex"
                  @click="next"
              >
                <font-icon class="next"></font-icon>
              </button>
            </nav>
          </footer>
        </infotainment-card>
      </li>
    </ul>
  </infotainment-slider>
</template>

<style scoped>
infotainment-slider {
  height: 100%;
}
</style>
